import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { BackgroundLocationService } from './background-location.service';
import { GeoLocationService } from './geoLocation.service';
import { LocalStorageService } from './localStorage.service';
import { AuthService } from './auth.service';
import { PusherService } from './pusher.service';
import { PusherBackgroundService } from './pusher-background.service';

@Injectable({
  providedIn: 'root'
})
export class AuthErrorService {
  private isLoggingOut = false;
  private isRefreshing = false;
  private logoutSubject = new BehaviorSubject<boolean>(false);
  public logout$ = this.logoutSubject.asObservable();

  constructor(
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    private router: Router,
    private geoLocationService: GeoLocationService,
    private backgroundLocationService: BackgroundLocationService,
    private pusherService: PusherService,
    private pusherBackgroundService: PusherBackgroundService
  ) {}

  /**
   * Gère une erreur d'authentification (401/403)
   * @param error L'erreur HTTP
   * @param request La requête HTTP qui a échoué
   */
  handleAuthError(error: any, request?: any): void {
    if (this.isLoggingOut || this.isRefreshing) {
      return;
    }

    if (error.status === 401 || error.status === 403) {
      this.attemptTokenRefresh();
    } else {
      this.performLogout();
    }
  }

  /**
   * Tente de rafraîchir le token en cas d'erreur 401/403 (une seule requête à la fois).
   */
  private attemptTokenRefresh(): void {
    if (this.isRefreshing || this.isLoggingOut) {
      return;
    }

    const refreshToken = this.localStorageService.getRefreshToken();
    if (!refreshToken) {
      this.performLogout();
      return;
    }

    this.isRefreshing = true;
    this.authService.refreshToken().subscribe({
      next: () => {
        this.isRefreshing = false;
      },
      error: () => {
        this.isRefreshing = false;
        if (!this.isLoggingOut) {
          this.performLogout();
        }
      },
    });
  }

  /**
   * Effectue la déconnexion complète de l'utilisateur
   */
  private performLogout(): void {
    if (this.isLoggingOut) {
      return;
    }
    this.isRefreshing = false;
    this.isLoggingOut = true;
    this.logoutSubject.next(true);

    if (!this.localStorageService.isAuth()) {
      this.cleanupAndRedirect();
      return;
    }

    this.authService.logout().subscribe({
      next: () => {
        // Logout API réussi
      },
      error: () => {
        // Même si l'API échoue, on continue avec le cleanup local
        this.cleanupAndRedirect();
      },
      complete: () => {
        this.cleanupAndRedirect();
      }
    });
  }

  /**
   * Nettoie le localStorage et redirige vers la page de connexion
   */
  private cleanupAndRedirect(): void {
    try {
      this.geoLocationService.stopTracking();
      this.backgroundLocationService.stopTracking().catch(() => {});
      this.pusherService.disconnectAndReset();
      // Live Activity iOS : terminer la carte lockscreen — sinon elle reste
      // affichée jusqu'au staleDate (8h) avec une session déconnectée.
      this.pusherBackgroundService.endLiveActivity().catch(() => {});
      this.localStorageService.logout();
    } catch (error) {
      // En cas d'erreur, forcer la navigation avec le router
      this.router.navigate(['/login'], { replaceUrl: true });
    } finally {
      this.isLoggingOut = false;
      this.logoutSubject.next(false);
    }
  }

  /**
   * Vérifie si une URL doit être exclue de la gestion des erreurs d'authentification
   * @param url L'URL à vérifier
   * @returns true si l'URL doit être exclue
   */
  isExcludedUrl(url: string): boolean {
    const excludedUrls = [
      '/login',
      '/register', 
      '/forgot-password',
      '/refresh-token',
      '/verifier-otp',
      '/reset-password'
    ];

    const hasAuthRoute = excludedUrls.some(excludedUrl => url.includes(excludedUrl));
    const isAuthEndpoint = url.includes('/auth/login') || 
                          url.includes('/auth/register') || 
                          url.includes('/auth/forgot-password') ||
                          url.includes('/auth/refresh-token') ||
                          url.includes('/auth/verifier-otp') ||
                          url.includes('/auth/reset-password');

    return hasAuthRoute || isAuthEndpoint;
  }

  /**
   * Vérifie si une erreur est une erreur d'authentification
   * @param error L'erreur à vérifier
   * @returns true si c'est une erreur d'authentification
   */
  isAuthError(error: any): boolean {
    return error && (error.status === 401 || error.status === 403);
  }
}
